import React, { useState, useEffect } from 'react';
import { getContentAndUrl } from '../link';
import { Jodit } from '@shared/core/jodit-react-ssr';
import { TranslateFunction } from '@locales/generated';

export type LinkFormTranslations = {
  insert: TranslateFunction;
  update: TranslateFunction;
  url: TranslateFunction;
  text: TranslateFunction;
  linkPlaceholder: TranslateFunction;
  unlink: TranslateFunction;
  updating: TranslateFunction;
  inserting: TranslateFunction;
};

export const LinkForm: React.FC<{
  onSubmit: (e: React.FormEvent<HTMLFormElement>, url: string, content: string, setLoading: (isLoading: boolean) => void) => void;
  isUpdate?: boolean;
  jodit: Jodit;
  translations: LinkFormTranslations;
  onUnlink?: () => void;
}> = ({ onSubmit, isUpdate, jodit, translations, onUnlink }) => {
  const [url, setUrl] = useState('');
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const { url, content } = getContentAndUrl(jodit);
    setUrl(url || '');
    setContent(content || '');
  }, [jodit]);

  const getButtonText = () => {
    if (isLoading) {
      return isUpdate ? translations.updating() : translations.inserting();
    }
    return isUpdate ? translations.update() : translations.insert();
  };

  return (
    <form
      style={{ padding: '12px' }}
      className="jodit-ui-form"
      dir="auto"
      onSubmit={e => {
        onSubmit(e, url, content, setIsLoading);
      }}
    >
      <div className="jodit-ui-block jodit-ui-block_align_left">
        <div className="jodit-ui-input jodit-ui-block__url">
          <span className="jodit-ui-input__label">{translations.url()}</span>
          <div className="jodit-ui-input__wrapper">
            <input
              value={url}
              onChange={e => setUrl(e.target.value)}
              className="jodit-ui-input__input"
              dir="auto"
              name="url"
              type="text"
              data-ref="url_input"
              required
              placeholder={translations.linkPlaceholder()}
            />
          </div>
        </div>
      </div>

      <div
        className="jodit-ui-block jodit-ui-block_align_left"
        data-ref="content_input_box"
      >
        <div className="jodit-ui-input jodit-ui-block__content">
          <span className="jodit-ui-input__label">{translations.text()}</span>
          <div className="jodit-ui-input__wrapper">
            <input
              value={content}
              onChange={e => setContent(e.target.value)}
              className="jodit-ui-input__input"
              dir="auto"
              name="content"
              type="text"
              data-ref="content_input"
            />
          </div>
        </div>
      </div>

      <div className="jodit-ui-block jodit-ui-block_align_full">
        {onUnlink && (
          <button
            className="jodit-ui-button jodit-ui-button_size_middle jodit-ui-button_unlink jodit-ui-button_variant_default jodit-ui-button_text-icons_true jodit-ui-block__unlink"
            type="button"
            role="button"
            onClick={onUnlink}
          >
            <span className="jodit-ui-button__icon"></span>
            <span className="jodit-ui-button__text">{translations.unlink()}</span>
          </button>
        )}

        <button
          className="jodit-ui-button jodit-ui-button_size_middle jodit-ui-button_insert jodit-ui-button_variant_primary jodit-ui-button_text-icons_true jodit-ui-block__insert"
          aria-pressed="false"
          data-ref="insert"
          type="submit"
          disabled={isLoading}
        >
          <span className="jodit-ui-button__icon"></span>
          <span className="jodit-ui-button__text">{getButtonText()}</span>
        </button>
      </div>
    </form>
  );
};

export default LinkForm;
